import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  IHospitalDetail,
  IMedicationSource,
  IMedicationSourceOption,
  ISnomedEdition,
  IUpdateHospitalRequest,
} from "../../../../@types/admin/AdminHospitalDetails";
import { useUpdateHospital } from "../../../../hooks/mutations/admin/hospitalDetails";
import {
  useMedicationSourceImpact,
  useMedicationSources,
} from "../../../../hooks/queries/admin/hospitalDetails";
import { useTenants } from "../../../../hooks/queries/admin/hospitals";
import { useCountryOptions } from "../../../../hooks/queries/admin/selectValues";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  AdminSelectField,
  AdminTimeField,
  CheckboxField,
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  FormGrid,
  FormSubsection,
  FormSubsectionTitle,
  SectionActions,
  SectionCaption,
  SectionCard,
  SectionHeader,
  SectionHeading,
  SectionTitle,
  WarningNotice,
} from "../../shared";
import {
  MAX_TEXT_LENGTH,
  TIMEZONES,
  toNotificationTime,
  toTimeInputValue,
} from "../hospitalFields";

interface IHospitalRecordFormProps {
  hospital: IHospitalDetail;
}

interface IHospitalFormState {
  name: string;
  code: string;
  countryId: string;
  tenantId: string;
  timezone: string;
  notificationTime: string;
  wardLabelSingular: string;
  wardLabelPlural: string;
  publish: boolean;
  enableAppointments: boolean;
  enableBilling: boolean;
  enableNebulizerAdherence: boolean;
  enableCareInfo: boolean;
  enableHseHeader: boolean;
  enableRag: boolean;
  hideLegacyAddStaffCheckboxes: boolean;
  /** `Sam`, `Snomed:<edition>`, or `Snomed` (no edition picked yet). */
  medicationSourceKey: string;
}

/**
 * One dropdown entry per catalogue and country (EN-5267): SAM, or SNOMED CT
 * with an edition. A bare `Snomed` is a hospital that never picked an
 * edition — its patients get their hospital country's edition.
 */
function toMedicationSourceKey(
  source: IMedicationSource,
  edition: ISnomedEdition | null | undefined
): string {
  if (source === "Sam") return "Sam";
  return edition ? `Snomed:${edition}` : "Snomed";
}

/** A bare `Snomed` sends `edition: null`, which clears a picked edition. */
function fromMedicationSourceKey(key: string): {
  source: IMedicationSource;
  edition?: ISnomedEdition | null;
} {
  if (key === "Sam") return { source: "Sam" };
  const [, edition] = key.split(":");
  return {
    source: "Snomed",
    edition: (edition as ISnomedEdition) || null,
  };
}

const COUNTRY_EDITION_KEY = "Snomed";

/** The warning's line on patients who already added medications. */
function affectedPatientsSentence(
  count: number | undefined,
  isLoading: boolean
) {
  if (count === undefined) {
    // A failed count leaves the rest of the warning standing on its own.
    return isLoading
      ? "Checking how many patients already added medications…"
      : "";
  }
  if (count === 0) {
    return "No patient of this hospital has added medications yet.";
  }
  const who = count === 1 ? "patient has" : "patients have";
  return `${count} ${who} already added medications from the current source; those keep their current name.`;
}

/** What the patient sees with each source; the choice changes every patient's search. */
function medicationSourceHint(option: IMedicationSourceOption | undefined) {
  if (!option) {
    return "Patients search the SNOMED CT edition of their hospital's country, plus SNOMED CT International.";
  }
  if (option.source === "Sam") {
    return "Belgian products from SAM, named in the patient's app language (French, otherwise Dutch). Never mixed with SNOMED CT.";
  }
  if (option.edition === "INT") {
    return "Patients search SNOMED CT International only. Never mixed with SAM.";
  }
  return `Patients search ${option.name} and SNOMED CT International only. Never mixed with SAM.`;
}

/**
 * The switches, in Retool's order, each with what it actually turns on.
 *
 * Retool rendered them as bare labels ("Rag", "Care Info", "HSE Header"),
 * which say nothing to an operator who has not seen the app. They are
 * hospital-wide feature flags — every patient and clinician of the hospital
 * sees the result — so each carries a line saying so.
 */
type IFeatureSwitchKey =
  | "enableAppointments"
  | "enableBilling"
  | "enableNebulizerAdherence"
  | "enableCareInfo"
  | "enableHseHeader"
  | "enableRag"
  | "hideLegacyAddStaffCheckboxes";

const FEATURE_SWITCHES: {
  key: IFeatureSwitchKey;
  label: string;
  hint: string;
}[] = [
  {
    key: "enableAppointments",
    label: "Appointments",
    hint: "Shows the appointments section to this hospital's patients and clinicians.",
  },
  {
    key: "enableBilling",
    label: "Billing",
    hint: "Enables the billing report for this hospital.",
  },
  {
    key: "enableNebulizerAdherence",
    label: "Nebulizer adherence",
    hint: "Shows nebulizer treatment adherence on the patient's record.",
  },
  {
    key: "enableCareInfo",
    label: "Care info",
    hint: "Shows the care-information section in the patient app.",
  },
  {
    key: "enableHseHeader",
    label: "HSE header",
    hint: "Applies the HSE-branded header to this hospital's patient app.",
  },
  {
    key: "enableRag",
    label: "RAG status",
    hint: "Shows the red/amber/green status column on the clinician patient list.",
  },
  {
    key: "hideLegacyAddStaffCheckboxes",
    label: "Hide legacy add-staff checkboxes",
    hint: "HSE tender: hides the pre-role permission checkboxes on the add-staff form.",
  },
];

function toFormState(hospital: IHospitalDetail): IHospitalFormState {
  return {
    name: hospital.name ?? "",
    code: hospital.code ?? "",
    countryId: hospital.countryId ? String(hospital.countryId) : "",
    tenantId: hospital.tenantId ?? "",
    timezone: hospital.timezone ?? "",
    notificationTime: toTimeInputValue(hospital.notificationTime),
    wardLabelSingular: hospital.wardLabelSingular ?? "",
    wardLabelPlural: hospital.wardLabelPlural ?? "",
    publish: hospital.publish,
    enableAppointments: hospital.enableAppointments,
    enableBilling: hospital.enableBilling,
    enableNebulizerAdherence: hospital.enableNebulizerAdherence,
    enableCareInfo: hospital.enableCareInfo,
    enableHseHeader: hospital.enableHseHeader,
    enableRag: hospital.enableRag,
    hideLegacyAddStaffCheckboxes: hospital.hideLegacyAddStaffCheckboxes,
    medicationSourceKey: toMedicationSourceKey(
      hospital.medicationSource ?? "Snomed",
      hospital.medicationEdition
    ),
  };
}

/**
 * The hospital record — Retool's `formHospitalDetails` (EN-5172).
 *
 * ⚠️ Every switch here is hospital-wide: it changes what every patient and
 * clinician of the hospital sees. `publish` is the strongest — un-publishing
 * takes the hospital out of the clinician sign-in flow.
 *
 * Two things Retool's PUT body did that this does not:
 *
 *  - It sent `order: 1` hard-coded, so saving the form reset the hospital's
 *    sort order. The record's own value is sent.
 *  - It sent `enabledOnboardingFields: {{ multiselect_onboardingFields.data }}`,
 *    bound to a component that exists NOWHERE in the export.
 *    `UpdateHospitalRequestDto` has no such property so the key was ignored,
 *    but it is not sent here either — onboarding fields are managed through
 *    `/Retool/CustomFields`, which is what the Custom fields tab opens.
 */
export function HospitalRecordForm({ hospital }: IHospitalRecordFormProps) {
  const { showToast } = useToast();
  const { updateHospital, isUpdatingHospital } = useUpdateHospital();
  const { countries } = useCountryOptions();
  const { tenants } = useTenants();
  const { medicationSources } = useMedicationSources();

  const [form, setForm] = useState<IHospitalFormState>(() =>
    toFormState(hospital)
  );

  useEffect(() => {
    setForm(toFormState(hospital));
  }, [hospital]);

  const errors = {
    name: form.name.trim() ? undefined : "Name is required.",
    code: form.code.trim() ? undefined : "Code is required.",
    countryId: form.countryId ? undefined : "Country is required.",
    tenantId: form.tenantId ? undefined : "Tenant is required.",
    timezone: form.timezone ? undefined : "Timezone is required.",
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const setField = <TKey extends keyof IHospitalFormState>(
    key: TKey,
    value: IHospitalFormState[TKey]
  ) => setForm((previous) => ({ ...previous, [key]: value }));

  const willUnpublish = hospital.publish && !form.publish;
  const savedMedicationSourceKey = toMedicationSourceKey(
    hospital.medicationSource ?? "Snomed",
    hospital.medicationEdition
  );
  const medicationSourceOptions = useMemo(() => {
    const options: {
      key: string;
      label: string;
      option: IMedicationSourceOption | undefined;
    }[] = medicationSources.map((option) => ({
      key: toMedicationSourceKey(option.source, option.edition),
      label: `${option.name} (${option.versionLabel})`,
      option,
    }));
    // Always selectable, so a picked edition can be cleared again.
    options.unshift({
      key: COUNTRY_EDITION_KEY,
      label: "SNOMED CT (hospital country's edition)",
      option: undefined,
    });
    // The saved value must stay selectable even when the list failed to load.
    if (!options.some((entry) => entry.key === savedMedicationSourceKey)) {
      options.unshift({
        key: savedMedicationSourceKey,
        label: savedMedicationSourceKey,
        option: undefined,
      });
    }
    return options;
  }, [medicationSources, savedMedicationSourceKey]);
  const selectedMedicationSource = medicationSourceOptions.find(
    (entry) => entry.key === form.medicationSourceKey
  );
  const changesMedicationSource =
    savedMedicationSourceKey !== form.medicationSourceKey;
  const changesMedicationCatalogue =
    (hospital.medicationSource ?? "Snomed") !==
    fromMedicationSourceKey(form.medicationSourceKey).source;
  const { impact, isLoading: isLoadingImpact } = useMedicationSourceImpact(
    Number(hospital.id),
    changesMedicationSource
  );
  const affectedPatients = impact?.patientsWithAddedMedications;

  const handleSave = async () => {
    if (hasErrors) return;

    const request: IUpdateHospitalRequest = {
      id: Number(hospital.id),
      name: form.name.trim(),
      code: form.code.trim(),
      timezone: form.timezone,
      countryId: Number(form.countryId),
      tenantId: form.tenantId,
      parentId: hospital.parentId,
      // Retool hard-coded 1 here, which reset the hospital's sort order on
      // every save.
      order: hospital.order,
      publish: form.publish,
      enableAppointments: form.enableAppointments,
      notificationTime: toNotificationTime(form.notificationTime),
      enableBilling: form.enableBilling,
      enableNebulizerAdherence: form.enableNebulizerAdherence,
      enableCareInfo: form.enableCareInfo,
      enableHseHeader: form.enableHseHeader,
      enableRag: form.enableRag,
      hideLegacyAddStaffCheckboxes: form.hideLegacyAddStaffCheckboxes,
      wardLabelSingular: form.wardLabelSingular.trim(),
      wardLabelPlural: form.wardLabelPlural.trim(),
      medicationSource: fromMedicationSourceKey(form.medicationSourceKey)
        .source,
      medicationEdition: fromMedicationSourceKey(form.medicationSourceKey)
        .edition,
    };

    try {
      await updateHospital({ hospitalId: Number(hospital.id), request });
      showToast("The hospital was updated successfully.", "success");
    } catch (error) {
      showToast(
        errorToastMessage(error, "There was an error updating the hospital."),
        "warning"
      );
    }
  };

  return (
    <SectionCard>
      <SectionHeader>
        <SectionHeading>
          <SectionTitle>Hospital details</SectionTitle>
          <SectionCaption>Hospital ID {hospital.id}</SectionCaption>
        </SectionHeading>
      </SectionHeader>

      <FormGrid>
        <FilterGroup>
          <FilterLabel htmlFor="hospital-name">Name</FilterLabel>
          <FilterInput
            id="hospital-name"
            type="text"
            maxLength={MAX_TEXT_LENGTH}
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
          />
          {errors.name && <FieldError>{errors.name}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="hospital-code">Code</FilterLabel>
          <FilterInput
            id="hospital-code"
            type="text"
            maxLength={MAX_TEXT_LENGTH}
            value={form.code}
            onChange={(e) => setField("code", e.target.value)}
          />
          {errors.code && <FieldError>{errors.code}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="hospital-country">Country</FilterLabel>
          <AdminSelectField
            id="hospital-country"
            value={form.countryId}
            onChange={(e) => setField("countryId", e.target.value)}
          >
            <option value="">Select a country</option>
            {countries.map((country) => (
              <option key={country.id} value={String(country.id)}>
                {country.name}
              </option>
            ))}
          </AdminSelectField>
          {errors.countryId && <FieldError>{errors.countryId}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="hospital-tenant">Tenant</FilterLabel>
          <AdminSelectField
            id="hospital-tenant"
            value={form.tenantId}
            onChange={(e) => setField("tenantId", e.target.value)}
          >
            <option value="">Select a tenant</option>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </AdminSelectField>
          {errors.tenantId && <FieldError>{errors.tenantId}</FieldError>}
          <FieldHint>
            The tenant decides the app&apos;s branding and which Cognito pool
            the hospital&apos;s users belong to.
          </FieldHint>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="hospital-timezone">Timezone</FilterLabel>
          <AdminSelectField
            id="hospital-timezone"
            value={form.timezone}
            onChange={(e) => setField("timezone", e.target.value)}
          >
            <option value="">Select a timezone</option>
            {TIMEZONES.map((timezone) => (
              <option key={timezone} value={timezone}>
                {timezone}
              </option>
            ))}
          </AdminSelectField>
          {errors.timezone && <FieldError>{errors.timezone}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="hospital-notification-time">
            Notification time
          </FilterLabel>
          <AdminTimeField
            id="hospital-notification-time"
            value={form.notificationTime}
            onChange={(next) => setField("notificationTime", next)}
          />
          <FieldHint>
            Local to the hospital&apos;s timezone, not UTC — when the daily
            patient reminders go out.
          </FieldHint>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="hospital-ward-singular">Ward label</FilterLabel>
          <FilterInput
            id="hospital-ward-singular"
            type="text"
            value={form.wardLabelSingular}
            onChange={(e) => setField("wardLabelSingular", e.target.value)}
          />
          <FieldHint>
            What this hospital calls a ward, shown throughout the clinician
            portal.
          </FieldHint>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="hospital-ward-plural">
            Ward label (plural)
          </FilterLabel>
          <FilterInput
            id="hospital-ward-plural"
            type="text"
            value={form.wardLabelPlural}
            onChange={(e) => setField("wardLabelPlural", e.target.value)}
          />
        </FilterGroup>

        <FilterGroup span="full">
          <CheckboxField>
            <input
              type="checkbox"
              checked={form.publish}
              onChange={(e) => setField("publish", e.target.checked)}
            />
            Published
          </CheckboxField>
        </FilterGroup>

        {willUnpublish && (
          <FilterGroup span="full">
            <WarningNotice>
              Un-publishing takes this hospital out of the clinician sign-in
              flow. Its staff will not be able to select it.
            </WarningNotice>
          </FilterGroup>
        )}

        {/* Retool listed these as bare switches with labels like "Rag". Each
            is a hospital-wide feature flag, so each says what it turns on. */}
        <FormSubsection>
          <FormSubsectionTitle>Features</FormSubsectionTitle>
          <FieldHint>
            These apply to every patient and clinician of this hospital, not
            just to this record.
          </FieldHint>
        </FormSubsection>

        {FEATURE_SWITCHES.map((entry) => (
          <FilterGroup key={entry.key}>
            <CheckboxField>
              <input
                type="checkbox"
                checked={form[entry.key]}
                onChange={(e) => setField(entry.key, e.target.checked)}
              />
              {entry.label}
            </CheckboxField>
            <FieldHint>{entry.hint}</FieldHint>
          </FilterGroup>
        ))}

        <FormSubsection>
          <FormSubsectionTitle>Medications</FormSubsectionTitle>
        </FormSubsection>

        <FilterGroup>
          <FilterLabel htmlFor="hospital-medication-source">
            Medication source
          </FilterLabel>
          <AdminSelectField
            id="hospital-medication-source"
            value={form.medicationSourceKey}
            onChange={(e) => setField("medicationSourceKey", e.target.value)}
          >
            {medicationSourceOptions.map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.label}
              </option>
            ))}
          </AdminSelectField>
          <FieldHint>
            {medicationSourceHint(selectedMedicationSource?.option)}
          </FieldHint>
          <FieldHint>
            This hospital&apos;s study medications and vitamins are managed
            under{" "}
            <Link to={`/admin/medications/custom?hospitalId=${hospital.id}`}>
              Medications → Custom medications
            </Link>
            .
          </FieldHint>
        </FilterGroup>

        {changesMedicationSource && (
          <FilterGroup span="full">
            <WarningNotice>
              {changesMedicationCatalogue
                ? `Every patient of this hospital will search and add medications from ${selectedMedicationSource?.label} instead, with no fallback to the other catalogue.`
                : `Patients of this hospital will search ${selectedMedicationSource?.label} instead.`}{" "}
              {affectedPatientsSentence(affectedPatients, isLoadingImpact)}
              {changesMedicationCatalogue &&
                " Migrate existing medications first if the care team needs them mapped."}
            </WarningNotice>
          </FilterGroup>
        )}
      </FormGrid>

      <SectionActions>
        <FilterButton
          variant="primary"
          disabled={isUpdatingHospital || hasErrors}
          onClick={handleSave}
        >
          {isUpdatingHospital ? "Saving..." : "Save hospital"}
        </FilterButton>
      </SectionActions>
    </SectionCard>
  );
}
